'use client';

import { TCustomIcon, THeroIcon } from '@/types';
import { useEffect, useState } from 'react';

interface IconProps {
  icon: THeroIcon | TCustomIcon;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Common Icon component that can render both HeroIcons and CustomIcons
 *
 * @param icon - Name of the icon to render from /assets/icons/
 * @param className - Optional Tailwind classes for styling the icon container
 * @param style - Optional inline styles for the icon container
 *
 * @example
 * // Render a heroicon
 * <Icon icon="calendar" className="w-6 h-6 text-blue-500" />
 */
export default function Icon({ icon, className = '', style }: IconProps) {
  const [svgContent, setSvgContent] = useState<string>('');

  useEffect(() => {
    if (icon.startsWith('custom-')) {
      fetch(`/assets/icons/custom/${icon}.svg`)
        .then(res => res.text())
        .then(svg => setSvgContent(svg))
        .catch(err => console.error('Failed to load custom icon:', err));
    } else {
      fetch(`/assets/icons/heroicons/${icon}.svg`)
        .then(res => res.text())
        .then(svg => setSvgContent(svg))
        .catch(err => console.error('Failed to load heroicon:', err));
    }
  }, [icon]);

  if (!svgContent) {
    return null;
  }

  return (
    <div
      className={`[&>svg]:w-full [&>svg]:h-full [&_path]:stroke-current [&_polyline]:stroke-current [&_circle]:stroke-current [&_line]:stroke-current [&_rect]:stroke-current [&_polygon]:stroke-current ${className}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
}
